import pointbreak from '@crossbeam/pointbreak';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { createHead } from '@unhead/vue/client';
import axios from 'axios';
import { EventEmitter } from 'eventemitter3';
import { DateTime } from 'luxon';
import { createPinia } from 'pinia';
import { posthog } from 'posthog-js';
import { createApp, h, resolveComponent } from 'vue';

import Crossbeam from '@/components/Crossbeam.vue';

import { setCustomHeader } from '@/api';
import { AuthService } from '@/auth';
import useIteratively from '@/composables/useIteratively';
import appConfig from '@/config';
import * as dash0 from '@/dash0';
import * as errors from '@/errors';
import initMinimalView from '@/initMinimalView';
import { ls } from '@/local_storage';
import { createRouter } from '@/router';
import {
  useBillingStore,
  useFeatureFlagStore,
  useFlashesStore,
  useNotificationsStore,
} from '@/stores';
import injectScripts from '@/third-party-scripts';
import { maybeEnableIntercom } from '@/trackers/intercom';
import {
  SSO_PARAM,
  configureGlobalSettings,
  getUrlParams,
  isLoginPopup,
  setIsLoginPopup,
} from '@/utils';

import { initializeMockServer } from './mocks';
import { createRootStore } from './stores/RootStore';

import '@/css/main.css';
configureGlobalSettings(window.CSRF_TOKEN);

axios.defaults.withCredentials = true;
axios.defaults.baseURL = appConfig.apiBaseUrl;

const setAxiosOrgHeader = (headerVal) => {
  if (headerVal) {
    setCustomHeader('Xbeam-Organization', headerVal);
    axios.defaults.headers.common['XBeam-Organization'] = headerVal;
  } else {
    setCustomHeader('Xbeam-Organization', null);
    delete axios.defaults.headers.common['XBeam-Organization'];
  }
};

/* Enable oauth-redirect for copilot login */
setIsLoginPopup();
// If we're not in a login popup and an sso param is present, put it in session storage
// for later
if (!isLoginPopup()) {
  const ssoParam = getUrlParams(SSO_PARAM)[0];
  if (ssoParam) {
    sessionStorage.setItem(SSO_PARAM, ssoParam);
  }
}
initMinimalView();

const startApp = async () => {
  await initializeMockServer();

  dash0.initDash0();
  const authService = new AuthService();

  const {
    iteratively,
    loadIteratively,
    setTrackingOptions,
    captureLoggedInUser,
  } = useIteratively();
  loadIteratively();

  const orgNotifier = new EventEmitter();
  orgNotifier.on('organizationFinalized', (org) => setAxiosOrgHeader(org.uuid));
  orgNotifier.on('organizationFinalized', (org) => ls.orgId.set(org.id));
  orgNotifier.on('organizationFinalized', (org) =>
    dash0.setDash0Organization({ id: org.id, uuid: org.uuid, name: org.name }),
  );

  const app = createApp(Crossbeam);
  app.use(createPinia());

  const store = createRootStore(orgNotifier, iteratively);

  await store.loadUserProfile({ initialLogin: true });
  setTrackingOptions(store.currentUser, store.currentOrg, false);
  captureLoggedInUser(store.currentUser, store.currentOrg);

  if (store.hasProfile) {
    dash0.identifyDash0User({
      userId: store.currentUser.id,
      email: store.currentUser.email,
      fullName: `${store.currentUser.first_name} ${store.currentUser.last_name}`,
    });
    const billingStore = useBillingStore();
    await billingStore.readySync;
  }

  if (window.Appcues && appConfig.appcuesEnabled && !!store.currentAuth) {
    window.Appcues.identify(store.currentUser.id, {
      email: store.currentUser.email,
      name: `${store.currentUser.first_name} ${store.currentUser.last_name}`,
      created_at: store.currentUser.created_at,
      core_role: store.currentAuth.role?.name ?? '',
      sales_role: store.currentAuth.sales_edge_role ?? '',
    });
  }

  if (posthog && appConfig.postHog && store.currentOrg?.id) {
    const { currentOrg, currentUser } = store;
    const isCrossbeamUser =
      currentUser?.email?.split('@')?.at(-1) === 'crossbeam.com';
    posthog.init(appConfig.postHog, {
      api_host: 'https://f.crossbeam.com',
      ui_host: 'https://us.posthog.com',
      defaults: '2026-01-30',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      before_send: isCrossbeamUser
        ? (cr) => (cr?.event === '$snapshot' ? cr : null)
        : undefined,
    });
    if (currentUser?.id)
      posthog.identify(currentUser.id.toString(), {
        email: currentUser.email,
        name: `${currentUser.first_name} ${currentUser.last_name}`,
      });
    posthog.group('organization', currentOrg.id.toString(), {
      name: currentOrg.name,
    });
  }

  const featureFlagStore = useFeatureFlagStore();
  await featureFlagStore.readySync;

  const notificationsStore = useNotificationsStore();
  notificationsStore.startPolling(30000);

  const router = createRouter(store);
  router.beforeEach((_to, _from, next) => {
    if (!window.Intercom) {
      next();
    } else {
      try {
        window.Intercom('update', {
          last_request_at: DateTime?.now()?.toSeconds(),
        });
      } catch (error) {
        console.error('Error updating Intercom:', error);
      } finally {
        next();
      }
    }
  });
  app.use(router);

  const head = createHead();
  app.use(head);
  app.use(pointbreak);

  app.config.globalProperties.$auth = authService;
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$iteratively = iteratively;

  app.component('FontAwesomeIcon', FontAwesomeIcon);

  /* workaround for https://github.com/vuejs/router/issues/1306 */
  app.component('RouterViewWrapper', (props, { slots }) =>
    h(resolveComponent('router-view'), props, slots),
  );

  const flashesStore = useFlashesStore();
  errors.configureHandlers(app, flashesStore);

  maybeEnableIntercom(store.currentUser, store.currentOrg, store.currentAuth);

  app.mount('#app');
};

startApp();

injectScripts();
